// EPOCH 21 PHASE 3: Real-time Parsing Preview Panel
// Live parsing preview with confidence indicators for CSR validation
// Date: August 1, 2025

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ConversationMessage, OutreachTeamMember } from '../../types/lead-responses';

interface ParsingPreviewPanelProps {
  rawConversation: string;
  teamMembers?: OutreachTeamMember[];
  onMessagesChanged?: (messages: ConversationMessage[]) => void;
  showConfidenceMetrics?: boolean;
  enableLivePreview?: boolean;
  validatedMessages?: ConversationMessage[]; // AI-validated messages take precedence
}

interface ParsedMessagePreview extends ConversationMessage {
  issues: string[];
  suggestions: string[];
}

const ParsingPreviewPanel: React.FC<ParsingPreviewPanelProps> = ({
  rawConversation,
  teamMembers: _teamMembers = [], // eslint-disable-line @typescript-eslint/no-unused-vars
  onMessagesChanged,
  showConfidenceMetrics = true,
  enableLivePreview = true,
  validatedMessages
}) => {
  const [parsedMessages, setParsedMessages] = useState<ParsedMessagePreview[]>([]);
  const [isParsingLive, setIsParsingLive] = useState(false);
  const [parsingStats, setParsingStats] = useState({
    totalMessages: 0,
    highConfidence: 0,
    mediumConfidence: 0,
    lowConfidence: 0,
    avgConfidence: 0,
    detectedLanguage: 'unknown' as 'english' | 'chinese' | 'mixed' | 'unknown'
  });

  // Live parsing function (frontend-only for real-time preview) - DISABLED: Using AI validation only
  /*
  const parseConversationLive = useCallback((rawText: string): ParsedMessagePreview[] => {
    if (!rawText.trim()) return [];

    console.log('🔍 EPOCH 21 PHASE 3: Live parsing preview...');
    
    const lines = rawText.split('\n').map(line => line.trim()).filter(line => line);
    const messages: ParsedMessagePreview[] = [];
    
    // Helper functions for live parsing
    const isDateLine = (line: string): boolean => {
      return /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)?\s*\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/i.test(line) ||
             /^\/\/\s*(Mon|Tue|Wed|Thu|Fri|Sat|Sun)?\s*\w{3}\s+\w{3}\s+\d{1,2}\s+\d{4}/i.test(line);
    };
    
    const isTimeLine = (line: string): boolean => {
      return /^\d{1,2}:\d{2}\s*(am|pm|AM|PM)$/.test(line);
    };
    
    const detectSenderName = (line: string): string | null => {
      if (line.includes('http') || line.includes('@') || line.includes('www.')) return null;
      
      // Multi-language name patterns
      const patterns = [
        /^([\u4e00-\u9fff]{2,4})$/,  // Chinese names
        /^([A-Z][A-Za-z]{1,14})$/,   // English single names
        /^([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*){0,3})$/ // English multi-word names
      ];
      
      for (const pattern of patterns) {
        const match = line.match(pattern);
        if (match) return match[1];
      }
      return null;
    };
    
    const analyzeSender = (blockLines: string[]): {
      senderName: string;
      isUserMessage: boolean;
      confidence: number;
      detectionMethod: string;
      issues: string[];
      suggestions: string[];
    } => {
      const content = blockLines.join('\n');
      let senderName = 'Unknown';
      let isUserMessage = false;
      let confidence = 20;
      let detectionMethod = 'fallback';
      const issues: string[] = [];
      const suggestions: string[] = [];
      
      // Check for explicit names
      for (let i = 0; i < blockLines.length; i++) {
        const potentialName = detectSenderName(blockLines[i]);
        if (potentialName) {
          // Check if it matches a team member
          const matchingMember = teamMembers.find(member => 
            member.team_member_name.toLowerCase() === potentialName.toLowerCase() ||
            member.aliases?.some((alias: OutreachTeamAlias) => 
              alias.alias_name.toLowerCase() === potentialName.toLowerCase()
            )
          );
          
          if (matchingMember) {
            senderName = matchingMember.team_member_name;
            isUserMessage = false;
            confidence = 90;
            detectionMethod = `explicit_team_match_line_${i}`;
          } else {
            senderName = potentialName;
            isUserMessage = true;
            confidence = 75;
            detectionMethod = `explicit_name_line_${i}`;
          }
          break;
        }
      }
      
      // Signature detection
      if (confidence < 50) {
        const signatureMatch = content.match(/(?:Thanks|Best|Regards),?\s*\n?([A-Za-z\u4e00-\u9fff\s]+)/i);
        if (signatureMatch) {
          const sigName = signatureMatch[1].trim();
          const matchingMember = teamMembers.find(member => 
            member.team_member_name.toLowerCase().includes(sigName.toLowerCase())
          );
          
          if (matchingMember) {
            senderName = matchingMember.team_member_name;
            isUserMessage = false;
            confidence = 80;
            detectionMethod = 'signature_team_match';
          } else {
            senderName = sigName;
            isUserMessage = true;
            confidence = 65;
            detectionMethod = 'signature_detection';
          }
        }
      }
      
      // Business language detection
      if (confidence < 50) {
        const businessPatterns = [
          /I'm\s+([A-Za-z\u4e00-\u9fff\s]+),?\s+(BD|Business|Sales)/i,
          /We\s+(help|provide|offer)/i,
          /Our\s+(company|team|solution)/i
        ];
        
        for (const pattern of businessPatterns) {
          if (pattern.test(content)) {
            senderName = teamMembers[0]?.team_member_name || 'Lead';
            isUserMessage = false;
            confidence = 60;
            detectionMethod = 'business_language';
            break;
          }
        }
      }
      
      // Generate issues and suggestions
      if (confidence < 50) {
        issues.push('Low confidence detection');
        suggestions.push('Consider adding sender name to team members or check message format');
      }
      
      if (senderName === 'Unknown') {
        issues.push('Unknown sender');
        suggestions.push('Add explicit sender name or update team member aliases');
      }
      
      if (content.length < 10) {
        issues.push('Very short message content');
        suggestions.push('Check if message was parsed correctly');
      }
      
      return {
        senderName,
        isUserMessage,
        confidence,
        detectionMethod,
        issues,
        suggestions
      };
    };
    
    // Main parsing loop
    let currentBlock: string[] = [];
    let currentDate = '';
    let currentTime = '';
    
    for (const line of lines) {
      if (isDateLine(line)) {
        // Process previous block
        if (currentBlock.length > 0) {
          const analysis = analyzeSender(currentBlock);
          messages.push({
            id: `msg-${messages.length}`,
            sender: analysis.isUserMessage ? 'user' : 'lead',
            sender_name: analysis.senderName,
            content: currentBlock.join('\n'),
            timestamp: currentDate && currentTime ? `${currentDate} ${currentTime}` : undefined,
            confidence: analysis.confidence,
            detectionMethod: analysis.detectionMethod,
            issues: analysis.issues,
            suggestions: analysis.suggestions
          });
        }
        currentDate = line;
        currentTime = '';
        currentBlock = [];
      } else if (isTimeLine(line)) {
        if (currentBlock.length > 0) {
          const analysis = analyzeSender(currentBlock);
          messages.push({
            id: `msg-${messages.length}`,
            sender: analysis.isUserMessage ? 'user' : 'lead',
            sender_name: analysis.senderName,
            content: currentBlock.join('\n'),
            timestamp: currentDate && currentTime ? `${currentDate} ${currentTime}` : undefined,
            confidence: analysis.confidence,
            detectionMethod: analysis.detectionMethod,
            issues: analysis.issues,
            suggestions: analysis.suggestions
          });
          currentBlock = [];
        }
        currentTime = line;
      } else {
        currentBlock.push(line);
      }
    }
    
    // Handle last block
    if (currentBlock.length > 0) {
      const analysis = analyzeSender(currentBlock);
      messages.push({
        id: `msg-${messages.length}`,
        sender: analysis.isUserMessage ? 'user' : 'lead',
        sender_name: analysis.senderName,
        content: currentBlock.join('\n'),
        timestamp: currentDate && currentTime ? `${currentDate} ${currentTime}` : undefined,
        confidence: analysis.confidence,
        detectionMethod: analysis.detectionMethod,
        issues: analysis.issues,
        suggestions: analysis.suggestions
      });
    }
    
    return messages;
    
  }, [teamMembers]);
  */

  // Parse conversation and update stats
  const updateParsing = useCallback(() => {
    setIsParsingLive(true);
    
    // Use validated messages if available, otherwise show placeholder
    let messages: ParsedMessagePreview[];
    if (validatedMessages && validatedMessages.length > 0) {
      // Convert validated messages to preview format
      messages = validatedMessages.map(msg => ({
        ...msg,
        issues: [],
        suggestions: ['AI-validated message']
      }));
    } else {
      // Rule-based parsing disabled - use AI validation instead
      messages = [];
    }
    
    setParsedMessages(messages);
    
    // Calculate stats
    const totalMessages = messages.length;
    const confidences = messages.map(m => m.confidence || 0);
    const avgConfidence = confidences.length ? confidences.reduce((a, b) => a + b, 0) / confidences.length : 0;
    
    const highConfidence = messages.filter(m => (m.confidence || 0) >= 80).length;
    const mediumConfidence = messages.filter(m => (m.confidence || 0) >= 50 && (m.confidence || 0) < 80).length;
    const lowConfidence = messages.filter(m => (m.confidence || 0) < 50).length;
    
    // Detect language
    const allContent = messages.map(m => m.content).join(' ');
    const chineseChars = (allContent.match(/[\u4e00-\u9fff]/g) || []).length;
    const englishChars = (allContent.match(/[a-zA-Z]/g) || []).length;
    
    let detectedLanguage: 'english' | 'chinese' | 'mixed' | 'unknown' = 'unknown';
    if (chineseChars > 0 && englishChars > 0) detectedLanguage = 'mixed';
    else if (chineseChars > englishChars) detectedLanguage = 'chinese';
    else if (englishChars > chineseChars) detectedLanguage = 'english';
    
    setParsingStats({
      totalMessages,
      highConfidence,
      mediumConfidence,
      lowConfidence,
      avgConfidence: Math.round(avgConfidence),
      detectedLanguage
    });
    
    // Notify parent component only if not using AI-validated messages
    // (to avoid overwriting AI validation results with empty array)
    if (onMessagesChanged && !(validatedMessages && validatedMessages.length > 0)) {
      onMessagesChanged(messages);
    }
    
    setTimeout(() => setIsParsingLive(false), 200); // Brief loading state
  }, [onMessagesChanged, validatedMessages]);

  // Auto-update parsing when conversation changes (if live preview enabled)
  useEffect(() => {
    if (enableLivePreview) {
      const debounceTimer = setTimeout(updateParsing, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [rawConversation, enableLivePreview, updateParsing]);

  // Update when validated messages change
  useEffect(() => {
    if (validatedMessages) {
      updateParsing();
    }
  }, [validatedMessages, updateParsing]);

  // Manual refresh
  const handleRefresh = useCallback(() => {
    updateParsing();
  }, [updateParsing]);

  // Confidence color helper - commented out as not currently used
  // const getConfidenceColor = (confidence: number): string => {
  //   if (confidence >= 80) return 'text-green-600 bg-green-50 border-green-200';
  //   if (confidence >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  //   return 'text-red-600 bg-red-50 border-red-200';
  // };

  const confidenceStats = useMemo(() => [
    { label: 'High (80%+)', count: parsingStats.highConfidence, color: 'bg-green-500' },
    { label: 'Medium (50-79%)', count: parsingStats.mediumConfidence, color: 'bg-yellow-500' },
    { label: 'Low (<50%)', count: parsingStats.lowConfidence, color: 'bg-red-500' }
  ], [parsingStats]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {validatedMessages && validatedMessages.length > 0 ? 'AI-Validated Preview' : 'Live Parsing Preview'}
            </h3>
            {validatedMessages && validatedMessages.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ AI Validated
              </span>
            )}
            {isParsingLive && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
                <span className="text-xs">Analyzing...</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="flex items-center text-xs text-gray-600">
              <input
                type="checkbox"
                checked={enableLivePreview}
                onChange={(e) => setIsParsingLive(e.target.checked)}
                className="mr-1"
              />
              Live Preview
            </label>
            <button
              onClick={handleRefresh}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Refresh parsing"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      {showConfidenceMetrics && parsingStats.totalMessages > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <span className="text-blue-800 font-medium">
                {parsingStats.totalMessages} messages
              </span>
              <span className="text-blue-700">
                Avg: {parsingStats.avgConfidence}%
              </span>
              <span className="text-blue-700 capitalize">
                {parsingStats.detectedLanguage}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {confidenceStats.map((stat, idx) => (
                <div key={idx} className="flex items-center space-x-1 text-xs">
                  <div className={`w-2 h-2 rounded-full ${stat.color}`}></div>
                  <span className="text-gray-600">{stat.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Preview */}
      <div className="max-h-96 overflow-y-auto">
        {parsedMessages.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {rawConversation.trim() ? 'Use "Advanced Validation" button to parse conversation with AI' : 'Enter conversation text and use Advanced Validation to parse'}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {parsedMessages.map((message, index) => {
              const isUser = message.sender === 'user' || message.sender_type === 'outreach_team';
              // Always show each message as separate, not grouped by sender
              
              return (
                <div key={message.id} className="mb-3 pb-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      isUser ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                    }`}>
                      {isUser ? 'U' : 'L'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-medium text-sm text-gray-900">
                          {message.sender_name}
                        </span>
                        {message.confidence && (
                          <span className={`text-xs ${
                            message.confidence >= 80 ? 'text-green-600' : 
                            message.confidence >= 50 ? 'text-yellow-600' : 
                            'text-red-600'
                          }`}>
                            {message.confidence}%
                          </span>
                        )}
                        {message.detectionMethod && (
                          <span className="text-xs text-gray-500">
                            {message.detectionMethod}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {message.content}
                      </div>
                    </div>
                  </div>
                  
                  {(message.issues.length > 0 || message.suggestions.length > 0) && (
                    <div className="ml-11 mt-2 text-xs space-y-1">
                      {message.issues.map((issue, idx) => (
                        <div key={idx} className="text-red-600 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {issue}
                        </div>
                      ))}
                      {message.suggestions.map((suggestion, idx) => (
                        <div key={idx} className="text-blue-600 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParsingPreviewPanel;